import { useMutation } from '@tanstack/react-query';
import type { ApiErrorBody } from '@web-config-app/api';
import {
  API_JSON_CONTENT_TYPE,
  getErrorDetail,
  parseJsonSafe,
} from '@web-config-app/api';
import { useAuthoringEnvironments } from '../use-authoring-environments/use-authoring-environments';
import { useConfigAppParams } from '../use-config-app-params/use-config-app-params';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';

export interface GenerateJsonLogicInput {
  prompt: string;
  agentName?: string;
}

type JsonLogicRule = Record<string, unknown>;
export interface GenerateJsonLogicResult {
  rule: JsonLogicRule;
  assumptions?: string;
}

interface GenerateJsonLogicError extends Error {
  assumptions?: string;
  agentResponseMessage?: string;
}

type AgentChatApiResponse = ApiErrorBody & {
  data?: {
    attributes?: {
      response?: string;
    };
  };
};

const createInvalidAgentResponseError = (
  assumptions?: string,
  agentResponseMessage?: string,
): GenerateJsonLogicError => {
  const error: GenerateJsonLogicError = new Error('INVALID_AGENT_RESPONSE');
  error.assumptions = assumptions;
  error.agentResponseMessage = agentResponseMessage;
  return error;
};

const isJsonLogicRule = (value: unknown): value is JsonLogicRule =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasRuleContent = (rule: JsonLogicRule): boolean =>
  Object.keys(rule).length > 0;

export const getGenerateJsonLogicAssumptions = (
  error: unknown,
): string | undefined => {
  if (!(error instanceof Error) || !('assumptions' in error)) {
    return undefined;
  }

  const { assumptions } = error;
  return typeof assumptions === 'string' && assumptions.trim()
    ? assumptions
    : undefined;
};

export const getGenerateJsonLogicAgentMessage = (
  error: unknown,
): string | undefined => {
  if (!(error instanceof Error) || !('agentResponseMessage' in error)) {
    return undefined;
  }

  const { agentResponseMessage } = error;
  return typeof agentResponseMessage === 'string' && agentResponseMessage.trim()
    ? agentResponseMessage
    : undefined;
};

const tryParseJsonLogic = (value: string): JsonLogicRule | undefined => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (isJsonLogicRule(parsed)) {
      return parsed;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

const toAssumptionsText = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }

  if (Array.isArray(value)) {
    const parts = value
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean);
    return parts.length ? parts.join(' ') : undefined;
  }

  return undefined;
};

const toStructuredRule = (value: unknown): JsonLogicRule | undefined => {
  if (isJsonLogicRule(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedRule = tryParseJsonLogic(value);
    if (parsedRule) {
      return parsedRule;
    }
  }

  return undefined;
};

const toGenerateResult = (parsed: JsonLogicRule): GenerateJsonLogicResult => {
  const assumptions = toAssumptionsText(parsed.assumptions);

  // Preferred contract: { rule: {...}, assumptions?: "..." }.
  if (Object.prototype.hasOwnProperty.call(parsed, 'rule')) {
    const rule = toStructuredRule(parsed.rule);
    if (!rule || !hasRuleContent(rule)) {
      throw createInvalidAgentResponseError(assumptions);
    }
    return {
      rule,
      assumptions,
    };
  }

  // If assumptions are top-level alongside rule operators, strip assumptions.
  if (Object.prototype.hasOwnProperty.call(parsed, 'assumptions')) {
    const ruleWithoutAssumptions: JsonLogicRule = Object.fromEntries(
      Object.entries(parsed).filter(([key]) => key !== 'assumptions'),
    );
    if (!hasRuleContent(ruleWithoutAssumptions)) {
      throw createInvalidAgentResponseError(assumptions);
    }
    return {
      rule: ruleWithoutAssumptions,
      assumptions,
    };
  }

  if (!hasRuleContent(parsed)) {
    throw createInvalidAgentResponseError();
  }

  return { rule: parsed, assumptions };
};

export const parseJsonLogicFromAgentResponse = (
  responseText: string,
): GenerateJsonLogicResult => {
  const trimmed = responseText.trim();
  const direct = tryParseJsonLogic(trimmed);
  if (direct) return toGenerateResult(direct);

  const fencedJsonMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fencedJsonMatch?.[1]) {
    const fenced = tryParseJsonLogic(fencedJsonMatch[1].trim());
    if (fenced) return toGenerateResult(fenced);
  }

  const firstBraceIndex = trimmed.indexOf('{');
  const lastBraceIndex = trimmed.lastIndexOf('}');
  if (firstBraceIndex >= 0 && lastBraceIndex > firstBraceIndex) {
    const bracketExtract = trimmed.slice(firstBraceIndex, lastBraceIndex + 1);
    const extracted = tryParseJsonLogic(bracketExtract);
    if (extracted) return toGenerateResult(extracted);
  }

  throw createInvalidAgentResponseError(undefined, trimmed);
};

export const useGenerateJsonLogic = () => {
  const { environmentKey } = useConfigAppParams();
  const { currentAuthoringEnvironment } = useAuthoringEnvironments({
    environmentKey,
  });
  const environmentFetch = useEnvironmentFetch(currentAuthoringEnvironment);
  const mutation = useMutation<
    GenerateJsonLogicResult,
    Error,
    GenerateJsonLogicInput
  >({
    mutationFn: async ({ prompt, agentName = 'jsonlogic_rule_agent' }) => {
      const trimmedPrompt = prompt.trim();
      if (!trimmedPrompt) {
        throw new Error('EMPTY_PROMPT');
      }

      const response = await environmentFetch('/v1/agent-chat', {
        method: 'POST',
        headers: {
          Accept: API_JSON_CONTENT_TYPE,
          'Content-Type': API_JSON_CONTENT_TYPE,
        },
        body: JSON.stringify({
          data: {
            type: 'agent-chat-request',
            attributes: {
              message: trimmedPrompt,
              agentName,
            },
          },
        }),
      });

      const { data } = await parseJsonSafe<AgentChatApiResponse>(
        response.clone(),
      );
      if (!response.ok) {
        throw new Error(getErrorDetail(data) ?? 'AGENT_CHAT_REQUEST_FAILED');
      }

      const responseText = data?.data?.attributes?.response;
      if (!responseText) {
        throw createInvalidAgentResponseError();
      }

      return parseJsonLogicFromAgentResponse(responseText);
    },
  });

  return {
    generateJsonLogic: mutation.mutateAsync,
    reset: mutation.reset,
  };
};
